import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { client, urlFor } from "../sanity/client";
import { useCart } from "../context/CartContext";

const PRODUCTS_QUERY = `*[_type == "product" && !(_id in path("drafts.**"))] | order(_createdAt desc) {
  _id, name, slug, category, image, price, description, inStock, featured,
  fullDescription,
  specs[] { label, value },
  useCases
}`;

const categories = [
  { value: "all", label: "All Products" },
  { value: "cables", label: "Cables & Wires" },
  { value: "chargers", label: "Chargers & Inverters" },
  { value: "solar", label: "Solar Panels & Pumps" },
  { value: "batteries", label: "Batteries & Storage" },
  { value: "iot", label: "IoT Devices" },
  { value: "spare-parts", label: "Spare Parts" },
  { value: "vehicles", label: "Vehicles" },
];

const categoryColors = {
  cables:       { color: "#2d6a4f", bg: "#eaf4ef", border: "#c2ddd0" },
  chargers:     { color: "#1e4d8c", bg: "#eaf0fb", border: "#bccfe9" },
  solar:        { color: "#8a5a0f", bg: "#faf3e5", border: "#e6cfa0" },
  batteries:    { color: "#4a3580", bg: "#f0ecfb", border: "#cdc3e8" },
  iot:          { color: "#0e6680", bg: "#e6f5f9", border: "#aad4e3" },
  "spare-parts":{ color: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe" },
  vehicles:     { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
};

const getTheme = (category) => categoryColors[category] || categoryColors["chargers"];

// ─── MODAL ───────────────────────────────────────────────────────────────────
function ProductModal({ product, onClose }) {
  const [activeTab, setActiveTab] = useState("details");
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const theme = getTheme(product.category);
  const catLabel = categories.find(c => c.value === product.category)?.label || product.category;

  // Close on Escape
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const imgSrc = product.image
    ? urlFor(product.image).width(900).height(400).fit("crop").auto("format").url()
    : null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: "640px",
          maxHeight: "92vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 40px 100px rgba(0,0,0,0.28)",
          animation: "modalIn 0.2s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* IMAGE */}
        <div style={{ position: "relative", flexShrink: 0, height: "220px", background: theme.bg, overflow: "hidden" }}>
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={product.name}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "64px", opacity: 0.2 }}>
              📦
            </div>
          )}

          {/* Gradient */}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)" }} />

          {/* Close */}
          <button
            onClick={onClose}
            style={{
              position: "absolute", top: "12px", right: "12px",
              width: "36px", height: "36px", borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              border: "1.5px solid rgba(255,255,255,0.35)",
              color: "white", fontSize: "16px", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              backdropFilter: "blur(4px)",
              fontFamily: "'Sora', sans-serif",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.35)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
          >
            ✕
          </button>

          {/* Badges + Title */}
          <div style={{ position: "absolute", bottom: "16px", left: "18px", right: "60px" }}>
            <div style={{ display: "flex", gap: "6px", marginBottom: "6px", flexWrap: "wrap" }}>
              <span style={{
                background: "white", color: theme.color,
                fontSize: "10px", fontWeight: "800",
                padding: "3px 10px", borderRadius: "6px",
                fontFamily: "'Sora', sans-serif",
                letterSpacing: "0.5px", textTransform: "uppercase",
                border: `1px solid ${theme.border}`,
              }}>
                {catLabel}
              </span>
              {product.inStock && (
                <span style={{
                  background: "#f0fdf4", color: "#16a34a",
                  fontSize: "10px", fontWeight: "700",
                  padding: "3px 10px", borderRadius: "6px",
                  fontFamily: "'Sora', sans-serif",
                  border: "1px solid #bbf7d0",
                }}>
                  In Stock ✓
                </span>
              )}
            </div>
            <h2 style={{
              color: "white", fontWeight: "800", fontSize: "22px",
              fontFamily: "'Sora', sans-serif", letterSpacing: "-0.4px",
              margin: 0, textShadow: "0 2px 8px rgba(0,0,0,0.4)",
            }}>
              {product.name}
            </h2>
          </div>
        </div>

        {/* TABS */}
        <div style={{ display: "flex", borderBottom: "1.5px solid #f0f0f0", padding: "0 20px", flexShrink: 0, background: "white" }}>
          {["details", "specs", "use cases"].map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              style={{
                padding: "13px 18px", border: "none", background: "none",
                cursor: "pointer", fontFamily: "'Sora', sans-serif",
                fontWeight: "800", fontSize: "12px",
                textTransform: "capitalize", letterSpacing: "0.3px",
                color: activeTab === t ? theme.color : "#9ca3af",
                borderBottom: `2.5px solid ${activeTab === t ? theme.color : "transparent"}`,
                transition: "color 0.15s",
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* TAB BODY */}
        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>

          {/* DETAILS */}
          {activeTab === "details" && (
            <>
              <p style={{
                fontSize: "14px", color: "#4b5563",
                fontFamily: "'DM Sans', sans-serif",
                lineHeight: "1.8", marginBottom: "20px", margin: "0 0 20px",
              }}>
                {product.fullDescription || product.description || "No description available."}
              </p>
              <div style={{
                background: theme.bg, borderRadius: "12px",
                padding: "14px 18px", border: `1px solid ${theme.border}`,
              }}>
                <div style={{ fontSize: "10px", color: "#9ca3af", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.6px", fontFamily: "'Sora', sans-serif", marginBottom: "4px" }}>
                  Brand
                </div>
                <div style={{ fontSize: "15px", fontWeight: "800", color: theme.color, fontFamily: "'Sora', sans-serif" }}>
                  Ajeevi Technologies
                </div>
              </div>
            </>
          )}

          {/* SPECS */}
          {activeTab === "specs" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {product.specs && product.specs.length > 0 ? (
                product.specs.map((s, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 16px",
                    background: i % 2 === 0 ? "#f8fafc" : "white",
                    borderRadius: "10px", border: "1px solid #f0f0f0",
                  }}>
                    <span style={{ fontSize: "13px", color: "#6b7280", fontFamily: "'DM Sans', sans-serif", fontWeight: "500" }}>
                      {s.label}
                    </span>
                    <span style={{ fontSize: "13px", fontWeight: "800", color: "#1a1a2e", fontFamily: "'Sora', sans-serif" }}>
                      {s.value}
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", fontFamily: "'DM Sans', sans-serif", fontSize: "14px" }}>
                  <div style={{ fontSize: "32px", marginBottom: "10px" }}>🔧</div>
                  Specs not added yet. Contact us for technical details.
                </div>
              )}
            </div>
          )}

          {/* USE CASES */}
          {activeTab === "use cases" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              {product.useCases && product.useCases.length > 0 ? (
                product.useCases.map((u, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: "10px",
                    padding: "14px 16px", background: theme.bg,
                    borderRadius: "12px", border: `1px solid ${theme.border}`,
                  }}>
                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: theme.color, flexShrink: 0 }} />
                    <span style={{ fontSize: "13px", color: theme.color, fontWeight: "700", fontFamily: "'Sora', sans-serif" }}>
                      {u}
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "40px 0", color: "#9ca3af", fontFamily: "'DM Sans', sans-serif", fontSize: "14px" }}>
                  <div style={{ fontSize: "32px", marginBottom: "10px" }}>📋</div>
                  Use cases not added yet.
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{
          padding: "16px 24px", borderTop: "1.5px solid #f0f0f0",
          display: "flex", alignItems: "center", gap: "12px",
          flexShrink: 0, background: "white",
        }}>
          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: "10px", color: "#9ca3af", fontFamily: "'DM Sans', sans-serif", fontWeight: "600", marginBottom: "2px", textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Price
            </div>
            {product.price ? (
              <div style={{ fontSize: "22px", fontWeight: "900", color: theme.color, fontFamily: "'Sora', sans-serif", letterSpacing: "-0.5px" }}>
                ₹{product.price.toLocaleString("en-IN")}
              </div>
            ) : (
              <div style={{ fontSize: "13px", color: "#9ca3af", fontFamily: "'DM Sans', sans-serif", fontWeight: "600" }}>
                Price on request
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: "10px", flex: 1, marginLeft: "4px" }}>
            <button
              onClick={() => { addToCart(product); onClose(); }}
              style={{
                flex: 1, padding: "13px", borderRadius: "12px", border: "none",
                background: "#2d6a4f", color: "white",
                fontFamily: "'Sora', sans-serif", fontWeight: "800",
                fontSize: "13px", cursor: "pointer", transition: "background 0.18s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#1a4a35"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#2d6a4f"}
            >
              + Add to Cart
            </button>
            <button
              onClick={() => { onClose(); navigate("/contact"); }}
              style={{
                flex: 1, padding: "13px", borderRadius: "12px",
                border: `1.5px solid ${theme.color}`,
                background: "white", color: theme.color,
                fontFamily: "'Sora', sans-serif", fontWeight: "800",
                fontSize: "13px", cursor: "pointer", transition: "all 0.18s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = theme.color; e.currentTarget.style.color = "white"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "white"; e.currentTarget.style.color = theme.color; }}
            >
              Get Quote
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.94) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ─── PRODUCT CARD ─────────────────────────────────────────────────────────────
function ProductCard({ product, onOpenModal }) {
  const [hovered, setHovered] = useState(false);
  const theme = getTheme(product.category);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const imgSrc = product.image
    ? urlFor(product.image).width(400).height(240).fit("crop").auto("format").url()
    : null;

  return (
    <div
      onClick={() => onOpenModal(product)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "white",
        borderRadius: "16px",
        border: `1.5px solid ${hovered ? theme.color + "55" : "#ebebeb"}`,
        overflow: "hidden",
        transition: "all 0.22s ease",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered
          ? `0 16px 40px ${theme.color}18`
          : "0 2px 8px rgba(0,0,0,0.04)",
        cursor: "pointer",
      }}
    >
      {/* IMAGE */}
      <div style={{
        height: "200px", background: theme.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", position: "relative",
      }}>
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={product.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div style={{ fontSize: "48px", opacity: 0.25 }}>📦</div>
        )}

        {/* Category badge */}
        <div style={{
          position: "absolute", top: "12px", left: "12px",
          background: "white", color: theme.color,
          fontSize: "10px", fontWeight: "800",
          padding: "4px 10px", borderRadius: "6px",
          fontFamily: "'Sora', sans-serif",
          letterSpacing: "0.5px", textTransform: "uppercase",
          border: `1px solid ${theme.border}`,
        }}>
          {categories.find(c => c.value === product.category)?.label || product.category}
        </div>

        {/* Stock badge */}
        {product.inStock && (
          <div style={{
            position: "absolute", top: "12px", right: "12px",
            background: "#f0fdf4", color: "#16a34a",
            fontSize: "10px", fontWeight: "700",
            padding: "4px 10px", borderRadius: "6px",
            fontFamily: "'Sora', sans-serif",
            border: "1px solid #bbf7d0",
          }}>
            In Stock ✓
          </div>
        )}

        {/* Hover overlay hint */}
        {hovered && (
          <div style={{
            position: "absolute", inset: 0,
            background: "rgba(0,0,0,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <div style={{
              background: "white", color: theme.color,
              fontSize: "12px", fontWeight: "800",
              padding: "7px 16px", borderRadius: "20px",
              fontFamily: "'Sora', sans-serif",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            }}>
              View Details →
            </div>
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div style={{ padding: "18px 20px" }}>
        <h3 style={{
          fontFamily: "'Sora', sans-serif", fontSize: "15px",
          fontWeight: "800", color: "#1a1a2e",
          margin: "0 0 8px", lineHeight: "1.3",
        }}>
          {product.name}
        </h3>

        {product.description && (
          <p style={{
            fontSize: "13px", color: "#6b7280",
            fontFamily: "'DM Sans', sans-serif",
            lineHeight: "1.6", margin: "0 0 14px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}>
            {product.description}
          </p>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
          {product.price ? (
            <div style={{
              fontFamily: "'Sora', sans-serif", fontSize: "17px",
              fontWeight: "900", color: theme.color,
            }}>
              ₹{product.price.toLocaleString("en-IN")}
            </div>
          ) : (
            <div style={{ fontSize: "13px", color: "#9ca3af", fontFamily: "'DM Sans', sans-serif", fontWeight: "600" }}>
              Price on request
            </div>
          )}

          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              onClick={(e) => { e.stopPropagation(); addToCart(product); }}
              style={{
                background: "#2d6a4f", color: "white", border: "none",
                borderRadius: "8px", padding: "7px 14px",
                fontSize: "12px", fontWeight: "700", cursor: "pointer",
                fontFamily: "'Sora', sans-serif", transition: "background 0.18s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#1a4a35"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#2d6a4f"}
            >
              + Add
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigate("/contact"); }}
              style={{
                background: theme.bg, color: theme.color,
                border: `1.5px solid ${theme.border}`,
                borderRadius: "8px", padding: "7px 16px",
                fontSize: "12px", fontWeight: "700", cursor: "pointer",
                fontFamily: "'Sora', sans-serif", transition: "all 0.18s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = theme.color; e.currentTarget.style.color = "white"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = theme.bg; e.currentTarget.style.color = theme.color; }}
            >
              Get Quote
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  const urlCategory = searchParams.get("category") || "all";
  const activeCategory = categories.find(c => c.value === urlCategory) ? urlCategory : "all";

  const setActiveCategory = (val) => {
    setSearchParams(val === "all" ? {} : { category: val });
  };

  useEffect(() => {
    client.fetch(PRODUCTS_QUERY)
      .then((data) => { setProducts(data); setLoading(false); })
      .catch((err) => { console.error("Sanity fetch error:", err); setLoading(false); });

    const subscription = client.listen(PRODUCTS_QUERY).subscribe(() => {
      client.fetch(PRODUCTS_QUERY).then((data) => setProducts(data));
    });

    return () => subscription.unsubscribe();
  }, []);

  const filtered = products.filter((p) => {
    const matchCategory = activeCategory === "all" || p.category === activeCategory;
    const matchSearch = p.name?.toLowerCase().includes(search.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <main style={{ background: "#f8fafc", minHeight: "100vh" }}>

      {/* MODAL */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {/* PAGE HEADER */}
      <div style={{ background: "white", borderBottom: "1px solid #e8eaed", padding: "40px 56px 32px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <p style={{
            fontSize: "11px", fontWeight: "800", color: "#aab0b8",
            letterSpacing: "2px", textTransform: "uppercase",
            fontFamily: "'Sora', sans-serif", marginBottom: "10px",
          }}>
            PHYSICAL PRODUCTS
          </p>
          <h1 style={{
            fontFamily: "'Sora', sans-serif",
            fontSize: "clamp(24px, 3vw, 36px)",
            fontWeight: "900", color: "#1a1a2e",
            letterSpacing: "-0.8px", margin: "0 0 8px",
          }}>
            Browse All Products
          </h1>
          <p style={{ color: "#6b7280", fontSize: "15px", fontFamily: "'DM Sans', sans-serif" }}>
            {products.length} products from verified Indian vendors
          </p>
        </div>
      </div>

      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px 56px" }}>

        {/* SEARCH */}
        <div style={{ position: "relative", marginBottom: "16px" }}>
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%", padding: "11px 44px 11px 16px",
              borderRadius: "10px", border: "1.5px solid #e5e7eb",
              fontSize: "14px", fontFamily: "'DM Sans', sans-serif",
              background: "white", outline: "none",
              transition: "border-color 0.2s", boxSizing: "border-box",
            }}
            onFocus={(e) => e.target.style.borderColor = "#2d6a4f"}
            onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
          />
          <span style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", fontSize: "16px" }}>🔍</span>
        </div>

        {/* FILTERS */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "32px" }}>
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(cat.value)}
              style={{
                padding: "8px 16px", borderRadius: "8px",
                border: `1.5px solid ${activeCategory === cat.value ? "#2d6a4f" : "#e5e7eb"}`,
                background: activeCategory === cat.value ? "#2d6a4f" : "white",
                color: activeCategory === cat.value ? "white" : "#374151",
                fontSize: "12px", fontWeight: "700", cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                transition: "all 0.18s ease", whiteSpace: "nowrap",
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* LOADING */}
        {loading && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{
              width: "40px", height: "40px",
              border: "3px solid #e5e7eb", borderTop: "3px solid #2d6a4f",
              borderRadius: "50%", animation: "spin 0.8s linear infinite",
              margin: "0 auto 16px",
            }} />
            <p style={{ color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>Loading products...</p>
          </div>
        )}

        {/* NO RESULTS */}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>📦</div>
            <h3 style={{ fontFamily: "'Sora', sans-serif", fontSize: "20px", fontWeight: "800", color: "#1a1a2e", marginBottom: "8px" }}>
              No products found
            </h3>
            <p style={{ color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>
              Try a different category or search term
            </p>
          </div>
        )}

        {/* GRID */}
        {!loading && filtered.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "20px" }}>
            {filtered.map((product) => (
              <ProductCard
                key={product._id}
                product={product}
                onOpenModal={setSelectedProduct}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </main>
  );
}

export default Products;